import type { FC } from 'react';
import React, { useCallback, memo, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { type GraphQLTaggedNode } from 'relay-runtime';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { LoadableAfterPaint } from '@confluence/loadable';

import type { SpaceLinks_spaceSidebarLinks$key } from './__generated__/SpaceLinks_spaceSidebarLinks.graphql.ts';
import { CALENDARS_KEY, QUESTIONS_NATIVE_KEY } from './webItemCompleteKeys.ts';

export const SpaceLinksSpaceSidebarLinksFragment: GraphQLTaggedNode = graphql`
	fragment SpaceLinks_spaceSidebarLinks on SpaceSidebarLinks {
		main(includeHidden: true) {
			id
			webItemCompleteKey
			position
			hidden
		}
	}
`;

type SpaceLink = {
	readonly hidden: boolean;
	readonly position: number;
	readonly webItemCompleteKey: string | null;
};

type SpaceLinksInnerProps = {
	links: ReadonlyArray<SpaceLink>;
};

type SpaceLinksProps =
	| (SpaceLinksInnerProps & {
			spaceSidebarLinksFragmentRef?: never;
	  })
	| {
			links?: never;
			spaceSidebarLinksFragmentRef: SpaceLinks_spaceSidebarLinks$key;
	  };

const SpaceLinksFromFragment: FC<{
	spaceSidebarLinksFragmentRef: SpaceLinks_spaceSidebarLinks$key;
}> = ({ spaceSidebarLinksFragmentRef }) => {
	const spaceSidebarLinks = useFragment(
		SpaceLinksSpaceSidebarLinksFragment,
		spaceSidebarLinksFragmentRef,
	);
	const links = useMemo(() => {
		const result: SpaceLink[] = [];
		for (const link of spaceSidebarLinks.main ?? []) {
			if (link) {
				result.push(link);
			}
		}
		return result;
	}, [spaceSidebarLinks.main]);

	return <SpaceLinksInner links={links} />;
};

export const SpaceLinks: FC<SpaceLinksProps> = (props) =>
	props.spaceSidebarLinksFragmentRef ? (
		<SpaceLinksFromFragment spaceSidebarLinksFragmentRef={props.spaceSidebarLinksFragmentRef} />
	) : (
		<SpaceLinksInner links={props.links} />
	);

const SpaceLinksInner: FC<SpaceLinksInnerProps> = memo(({ links }) => {
	const routeDataRef = useRouteDataRef();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();

	const isPremiumEdition = edition === ConfluenceEdition.PREMIUM;

	const sendItemClickedAnalytics = useCallback(
		(itemId: string) => () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'spaceNavigationItem',
					source: 'containerNavigation',
					attributes: {
						navigationLayer: 'container',
						itemId,
						selectedItemPageContext: routeDataRef.current?.routeName,
						navVersion: '4',
						isPremiumEdition,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, routeDataRef, isPremiumEdition],
	);

	const renderedLinks = useMemo(() => {
		const builtInitemsMapping: Record<string, React.ReactNode> = {
			[CALENDARS_KEY]: (
				<CalendarsLink key="calendars-link" onClick={sendItemClickedAnalytics('calendars')} />
			),
			[QUESTIONS_NATIVE_KEY]: (
				<QuestionsLink
					key="questions-native-link"
					onClick={sendItemClickedAnalytics('questions')}
				/>
			),
		};

		return links
			.filter((link) => !link.hidden)
			.slice()
			.sort((a, b) => Number(a.position) - Number(b.position))
			.map(
				({ webItemCompleteKey }) =>
					(webItemCompleteKey && builtInitemsMapping[webItemCompleteKey]) ||
					/* Hide everything else */ null,
			);
	}, [links, sendItemClickedAnalytics]);

	return <>{renderedLinks}</>;
});

const CalendarsLink = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-CalendarsLink" */ './CalendarsLink.tsx'))
			.CalendarsLink,
});

const QuestionsLink = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-QuestionsLink" */ './QuestionsLink.tsx'))
			.QuestionsLink,
});
